// IndexedDB Schema for TrackProfit Offline Storage
// This defines the database structure with security and sync capabilities

export const SYNC_STATUS = {
  SYNCED: 'SYNCED',
  PENDING_CREATE: 'PENDING_CREATE',
  PENDING_UPDATE: 'PENDING_UPDATE',
  PENDING_DELETE: 'PENDING_DELETE',
  CONFLICT: 'CONFLICT',
  TAMPERED: 'TAMPERED'
};

export const SYNC_ACTIONS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE'
};

export const ENTITY_TYPES = {
  ORDER: 'ORDER',
  EXPENSE: 'EXPENSE',
  CUSTOMER: 'CUSTOMER',
  PRODUCT: 'PRODUCT',
  MONEY_ACCOUNT: 'MONEY_ACCOUNT',
  CATEGORY: 'CATEGORY',
  ORG_ACCOUNT: 'ORG_ACCOUNT'
};

// Database schema definition
export const schema = {
  // Orders table - main business transactions
  orders: {
    primaryKey: 'orderId',
    indexes: [
      'orgId',
      'accountId', 
      'syncStatus',
      'localModifiedAt',
      'serverModifiedAt',
      ['orgId', 'accountId'],
      ['orgId', 'syncStatus'],
      ['accountId', 'syncStatus']
    ],
    fields: {
      orderId: 'string',
      orgId: 'string',
      accountId: 'string',
      name: 'string',
      productId: 'string',
      customerId: 'string',
      customerName: 'string',
      date: 'string', // ISO date string
      customLines: 'array', // array of {quantity, price, name}
      items: 'array', // array of {productId, name, price, quantity, unit, discountPercent}
      sellingPrice: 'number',
      discount: 'number', // overall discount amount
      discountPercent: 'number', // overall discount percentage
      totalCost: 'number',
      cashReceived: 'number',
      cashPending: 'number',
      money_account_id: 'string',
      incomeCategory: 'string',
      syncStatus: 'string', // SYNC_STATUS enum
      localModifiedAt: 'number', // timestamp
      serverModifiedAt: 'number', // timestamp
      conflictData: 'object', // server version when conflict
      dataHash: 'string', // SHA-256 hash for integrity
      createdDate: 'number',
      updatedDate: 'number'
    }
  },

  // Expenses table - business expenses
  expenses: {
    primaryKey: 'expenseId',
    indexes: [
      'orgId',
      'accountId',
      'syncStatus',
      'localModifiedAt',
      'serverModifiedAt',
      ['orgId', 'accountId'],
      ['orgId', 'syncStatus']
    ],
    fields: {
      expenseId: 'string',
      orgId: 'string',
      accountId: 'string',
      category: 'string',
      amount: 'number',
      date: 'string', // ISO date string
      frequency: 'string', // daily, weekly, monthly
      note: 'string',
      syncStatus: 'string',
      localModifiedAt: 'number',
      serverModifiedAt: 'number',
      conflictData: 'object',
      dataHash: 'string',
      createdDate: 'number',
      updatedDate: 'number'
    }
  },

  // Customers table - customer information
  customers: {
    primaryKey: 'customerId',
    indexes: [
      'orgId',
      'syncStatus',
      'name',
      'phone',
      ['orgId', 'name'],
      ['orgId', 'phone']
    ],
    fields: {
      customerId: 'string',
      orgId: 'string',
      name: 'string',
      phone: 'string',
      address: 'string',
      customerNumber: 'string',
      email: 'string',
      syncStatus: 'string',
      localModifiedAt: 'number',
      serverModifiedAt: 'number',
      conflictData: 'object',
      dataHash: 'string',
      createdDate: 'number',
      updatedDate: 'number'
    }
  },

  // Products table - unified products and inventory
  products: {
    primaryKey: 'productId',
    indexes: [
      'orgId',
      'syncStatus',
      'name',
      ['orgId', 'name'],
      ['orgId', 'syncStatus']
    ],
    fields: {
      productId: 'string',
      orgId: 'string',
      name: 'string',
      price: 'number', // selling price
      cost: 'number', // buying cost
      unit: 'string', // kg, piece, liter, etc.
      description: 'string',
      discountPercent: 'number', // default discount percentage for this product
      linkedItems: 'array', // inventory items if tracked
      syncStatus: 'string',
      localModifiedAt: 'number',
      serverModifiedAt: 'number',
      conflictData: 'object',
      dataHash: 'string',
      createdDate: 'number',
      updatedDate: 'number'
    }
  },

  // Money accounts table - financial accounts
  moneyAccounts: {
    primaryKey: 'accountId',
    indexes: [
      'orgId',
      'syncStatus',
      ['orgId', 'syncStatus']
    ],
    fields: {
      accountId: 'string',
      orgId: 'string',
      name: 'string',
      balance: 'number',
      syncStatus: 'string',
      localModifiedAt: 'number',
      serverModifiedAt: 'number',
      conflictData: 'object',
      dataHash: 'string',
      createdDate: 'number',
      updatedDate: 'number'
    }
  },

  // Categories table - expense and income categories
  categories: {
    primaryKey: 'categoryId',
    indexes: [
      'orgId',
      'type',
      'syncStatus',
      ['orgId', 'type'],
      ['orgId', 'syncStatus']
    ],
    fields: {
      categoryId: 'string',
      orgId: 'string',
      name: 'string',
      type: 'string', // 'expense' or 'income'
      syncStatus: 'string',
      localModifiedAt: 'number',
      serverModifiedAt: 'number',
      conflictData: 'object',
      dataHash: 'string',
      createdDate: 'number',
      updatedDate: 'number'
    }
  },

  // Organization accounts table - business accounts
  orgAccounts: {
    primaryKey: 'accountId',
    indexes: [
      'orgId',
      'syncStatus',
      ['orgId', 'syncStatus']
    ],
    fields: {
      accountId: 'string',
      orgId: 'string',
      name: 'string',
      description: 'string',
      createdBy: 'string',
      primaryAdmin: 'string',
      syncStatus: 'string',
      localModifiedAt: 'number',
      serverModifiedAt: 'number',
      conflictData: 'object',
      dataHash: 'string',
      createdDate: 'number',
      updatedDate: 'number'
    }
  },

  // Sync queue table - pending sync operations
  syncQueue: {
    primaryKey: 'queueId',
    indexes: [
      'entity',
      'entityId',
      'createdAt',
      'retryCount'
    ],
    fields: {
      queueId: 'string',
      action: 'string', // SYNC_ACTIONS enum
      entity: 'string', // ENTITY_TYPES enum
      entityId: 'string',
      payload: 'object', // data to sync
      retryCount: 'number',
      createdAt: 'number',
      signature: 'string', // HMAC signature for security
      lastAttempt: 'number'
    }
  },

  // User permissions table - cached permissions
  userPermissions: {
    primaryKey: 'userId',
    indexes: [
      'accountId',
      'cachedAt'
    ],
    fields: {
      userId: 'string',
      accountId: 'string',
      role: 'string',
      permissions: 'array', // array of permission strings
      cachedAt: 'number',
      signature: 'string', // HMAC signature from server
      expiresAt: 'number'
    }
  },

  // Metadata table - app state and sync info
  metadata: {
    primaryKey: 'key',
    fields: {
      key: 'string',
      value: 'string', // JSON string
      updatedAt: 'number'
    }
  }
};

// Default values for new records
export const getDefaultValues = (entityType) => {
  const now = Date.now();
  const baseDefaults = {
    syncStatus: SYNC_STATUS.PENDING_CREATE,
    localModifiedAt: now,
    serverModifiedAt: 0,
    conflictData: null,
    dataHash: '',
    createdDate: now,
    updatedDate: now
  };

  switch (entityType) {
    case ENTITY_TYPES.ORDER:
      return {
        ...baseDefaults,
        customLines: [],
        items: [],
        sellingPrice: 0,
        discount: 0,
        discountPercent: 0,
        totalCost: 0,
        cashReceived: 0,
        cashPending: 0,
        incomeCategory: 'Sales'
      };
    case ENTITY_TYPES.EXPENSE:
      return {
        ...baseDefaults,
        amount: 0,
        frequency: 'daily'
      };
    case ENTITY_TYPES.CUSTOMER:
      return {
        ...baseDefaults,
        name: '',
        phone: '',
        address: '',
        email: ''
      };
    case ENTITY_TYPES.PRODUCT:
      return {
        ...baseDefaults,
        name: '',
        price: 0,
        cost: 0,
        unit: 'piece',
        description: '',
        discountPercent: 0,
        linkedItems: []
      };
    case ENTITY_TYPES.MONEY_ACCOUNT:
      return {
        ...baseDefaults,
        name: '',
        balance: 0
      };
    case ENTITY_TYPES.CATEGORY:
      return {
        ...baseDefaults,
        name: '',
        type: 'expense'
      };
    case ENTITY_TYPES.ORG_ACCOUNT:
      return {
        ...baseDefaults,
        name: '',
        description: '',
        createdBy: '',
        primaryAdmin: ''
      };
    default:
      return baseDefaults;
  }
};



